import { useCallback, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  reload,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  updatePassword,
  updateProfile,
  type User,
} from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, firestore, functionsEu, httpsCallable } from '../firebase';
import { ensureFirestoreUserDocument } from '../utils/userBootstrap';
import { PASSWORD_RESET_ACTION_CODE_SETTINGS } from '../utils/authActionSettings';
import { callFunctionViaFetch } from '../utils/callFunctionViaFetch';
import { removePushTokenForCurrentDevice } from './usePushNotifications';
import { translateCurrent, useI18n } from '../i18n';
import { performGoogleSignIn, signOutGoogle } from './googleSignIn';
import { performAppleSignIn } from './appleSignIn';

// Robust-enough practical email validation (not full RFC 5322, which is
// impractical to enforce client-side and rejects nothing a real mail server
// wouldn't already reject) — requires a local part, an @, a domain with a
// dot, and a TLD of at least 2 characters. Matches the pattern the backend
// (functions/index.js) and the web client already validate against, so a
// client-accepted address is never rejected server-side for format reasons.
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function isValidEmailFormat(email: string): boolean {
  return EMAIL_REGEX.test(email.trim());
}

// Re-exported for API compatibility — was previously defined inline here;
// now lives in ./googleSignIn (shared with its .web.ts counterpart).
export { configureGoogleSignIn } from './googleSignIn';

// Takes no `t` parameter on purpose — this can be (and is) called from
// useAuth()'s own sign-in callbacks, which run ABOVE <I18nProvider> in
// App.tsx (useAuth() is instantiated before the provider tree mounts, see
// App.tsx). useI18n()'s `t` in that position silently resolves to
// I18nContext's default stub (`(key, fallback) => fallback || key`), which
// has no dictionary at all — every error message built with it was
// rendering as its own raw key (reproduced live: "auth.methodNotEnabled"
// shown verbatim in the Apple Sign-in-failed alert). translateCurrent()
// reads the active-language dictionaries directly via a module-level
// variable instead of React Context, so it resolves correctly regardless of
// where in the component tree it's called from — the same reason it's
// already used for other outside-of-render-tree translation needs (e.g.
// useNotificationListeners.ts's foreground banners).
function getFirebaseErrorMessage(error: unknown): string {
  const code =
    typeof error === 'object' && error !== null && 'code' in error
      ? String((error as { code?: string }).code)
      : '';

  switch (code) {
    case 'auth/email-already-in-use':
      return translateCurrent('auth.emailAlreadyInUse');
    case 'auth/invalid-email':
      return translateCurrent('auth.invalidEmail');
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return translateCurrent('auth.invalidCredentials');
    case 'auth/weak-password':
      return translateCurrent('auth.weakPassword');
    case 'auth/operation-not-allowed':
      return translateCurrent('auth.methodNotEnabled');
    case 'auth/network-request-failed':
      return translateCurrent('auth.networkError');
    case 'resource-exhausted':
    case 'functions/resource-exhausted':
      return translateCurrent('verifyEmail.resendRateLimited');
    case 'tonyte/popup-timeout':
      // OAuthPopupTimeoutError (see oauthPopupTimeout.ts) — no dedicated
      // copy exists for this yet, and "Sign-in failed. Please try again."
      // already reads correctly for a stalled popup; reused rather than
      // adding new English text for a single, fairly rare case.
      return translateCurrent('auth.signInFailed');
    default:
      return translateCurrent('auth.signInFailed');
  }
}

function isEmailPasswordAccount(user: User | null): boolean {
  return Boolean(user?.providerData.some(provider => provider.providerId === 'password'));
}

// Providers Firebase itself only ever asserts with an already-verified
// email — an account with one of these linked is trustworthy for Tonyte's
// verification-gate purposes regardless of what the 'password' provider's
// own (possibly still-unverified) state looks like. This is the
// account-linking guard: providerData lists EVERY provider ever linked to
// the account, not just the one used for the current session, so a
// password account that later links Google/Apple keeps 'password' in this
// list forever — checking for 'password' alone would wrongly keep gating an
// account Firebase (and the user) already trust. Firestore rules don't need
// the equivalent check: they read request.auth.token.email_verified, the
// account-wide flag, which Firebase itself flips to true on such a link.
const TRUSTED_VERIFIED_PROVIDER_IDS = new Set(['google.com', 'apple.com']);

function hasTrustedLinkedProvider(user: User | null): boolean {
  return Boolean(user?.providerData.some(provider => TRUSTED_VERIFIED_PROVIDER_IDS.has(provider.providerId)));
}

export function useAuth() {
  const { t } = useI18n();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // Mirrors user?.emailVerified, but as its own state — reload() (used by
  // reloadUser() below) mutates the existing User object in place rather
  // than firing onAuthStateChanged again, so reading user.emailVerified
  // directly would never re-render after a successful "Check again".
  const [emailVerified, setEmailVerified] = useState(true);
  // True from the instant a login/signup action is tapped until Firebase
  // Auth actually resolves (success -> onAuthStateChanged sets `user` and
  // clears this below; failure -> the catch block in each sign-in method
  // clears it). AppNavigator treats this the same as "auth is still
  // resolving" so the loading screen appears immediately on tap instead of
  // the login screen staying visible (with just a local button spinner)
  // until the network round trip finishes.
  const [isSigningIn, setIsSigningIn] = useState(false);

  useEffect(() => {
    if (__DEV__) {
      console.log('[Tonyte][AuthBoot] app started');
      console.log('[Tonyte][AuthBoot] waiting for Firebase auth');
    }

    const unsubscribe = onAuthStateChanged(auth, async currentUser => {
      if (__DEV__) {
        console.log('[Tonyte][AuthBoot] auth state listener fired', {
          uid: currentUser?.uid ?? null,
          email: currentUser?.email ?? null,
          providers: currentUser?.providerData.map(provider => provider.providerId) ?? [],
        });
      }

      if (currentUser) {
        if (__DEV__) {
          console.log('[Tonyte][AuthBoot] authenticated user resolved; starting Firestore bootstrap', {
            uid: currentUser.uid,
          });
        }
        try {
          await ensureFirestoreUserDocument(currentUser);
          if (__DEV__) {
            console.log('[Tonyte][AuthBoot] Firestore bootstrap complete', {
              uid: currentUser.uid,
            });
          }
        } catch (error) {
          console.error('[Tonyte][AuthBoot] continuing after Firestore bootstrap failure', {
            uid: currentUser.uid,
            code: typeof error === 'object' && error !== null && 'code' in error
              ? String((error as { code?: unknown }).code || '')
              : '',
            message: error instanceof Error ? error.message : String(error),
          });
        }
        setUser(currentUser);
        setEmailVerified(currentUser.emailVerified);
        setLoading(false);
        setIsSigningIn(false);
        if (__DEV__) {
          console.log('[Tonyte][AuthBoot] authenticated app state published', {
            uid: currentUser.uid,
          });
        }
        return;
      }

      if (__DEV__) console.log('[Tonyte][AuthBoot] no user, show login');
      setUser(null);
      setEmailVerified(true);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Resolves a Partner ID (TONYTE-CITY-0000) to its account email via the
  // thin, unauthenticated resolvePartnerIdentifier callable (also used by
  // the website) — it never checks a password or issues a token, so this is
  // the ONLY custom step before a completely normal
  // signInWithEmailAndPassword call below. A plain email is passed through
  // unresolved (the callable also does this itself, but skipping the round
  // trip for the common case is a cheap win) — there is no separate partner
  // login system, just this one extra identifier-resolution step ahead of
  // the same sign-in every account uses.
  const resolveSignInIdentifier = useCallback(async (identifier: string) => {
    const trimmed = identifier.trim();
    if (!/^TONYTE-[A-Z0-9]+-\d{4,}$/i.test(trimmed)) return trimmed;
    try {
      const resolvePartnerIdentifier = httpsCallable<{ identifier: string }, { email: string }>(
        functionsEu,
        'resolvePartnerIdentifierEu',
      );
      const result = await resolvePartnerIdentifier({ identifier: trimmed });
      return result.data?.email || trimmed;
    } catch {
      return trimmed;
    }
  }, []);

  const signInWithEmail = useCallback(
    async (email: string, password: string, mode: 'signin' | 'signup') => {
      setIsSigningIn(true);
      try {
        const normalizedEmail = mode === 'signin'
          ? await resolveSignInIdentifier(email)
          : email.trim();
        if (mode === 'signup') {
          if (!isValidEmailFormat(normalizedEmail)) {
            throw new Error(t('auth.invalidEmail'));
          }
          const credential = await createUserWithEmailAndPassword(
            auth,
            normalizedEmail,
            password,
          );
          const displayName = normalizedEmail.split('@')[0];
          await updateProfile(credential.user, { displayName });
          setEmailVerified(credential.user.emailVerified);
          // Best-effort: the account already exists and the user is signed
          // in regardless of whether this send succeeds — a failure here
          // must not undo signup. The Verify Email screen's own Resend
          // button covers a failed/lost send.
          try {
            const requestEmailVerificationEmail = httpsCallable(functionsEu, 'requestEmailVerificationEmailEu');
            await requestEmailVerificationEmail();
          } catch (verificationError) {
            console.warn('[Tonyte][Auth] failed to send initial verification email', verificationError);
          }
          return;
        }

        await signInWithEmailAndPassword(auth, normalizedEmail, password);
      } catch (error) {
        setIsSigningIn(false);
        throw new Error(getFirebaseErrorMessage(error));
      }
    },
    [resolveSignInIdentifier, t],
  );

  // First-login completion for accounts created before the invitation flow
  // (see acceptPartnerInvitation in functions/index.js) that still have
  // mustChangePassword: true — sets a new password on the already-
  // authenticated session, then flips partnerProfiles/{uid}.mustChangePassword
  // to false, the one field firestore.rules' isOwnMustChangePasswordCompletion
  // allows a partner to self-toggle. New invitations never hit this: the
  // password is already set during activation, before the account is ever
  // marked active.
  const completePartnerPasswordSetup = useCallback(async (newPassword: string) => {
    if (!auth.currentUser) {
      throw new Error(t('auth.notSignedIn'));
    }
    try {
      await updatePassword(auth.currentUser, newPassword);
    } catch (error) {
      throw new Error(getFirebaseErrorMessage(error));
    }
    await setDoc(
      doc(firestore, 'partnerProfiles', auth.currentUser.uid),
      { mustChangePassword: false, updatedAt: serverTimestamp() },
      { merge: true },
    );
  }, [t]);

  const resetPassword = useCallback(async (email: string) => {
    try {
      await callFunctionViaFetch(
        'https://europe-west3-triptribe-8babd.cloudfunctions.net/requestPasswordResetEmailEu',
        { email: email.trim(), continueUrl: PASSWORD_RESET_ACTION_CODE_SETTINGS.url },
      );
    } catch (error) {
      throw new Error(getFirebaseErrorMessage(error));
    }
  }, [t]);

  const signInWithGoogle = useCallback(async () => {
    setIsSigningIn(true);
    try {
      const userCredential = await performGoogleSignIn(t);
      if (__DEV__) {
        console.log('[GoogleSignIn] Firebase sign-in success', {
          uid: userCredential.user.uid,
          email: userCredential.user.email || '',
          isNewUser: userCredential.user.metadata.creationTime === userCredential.user.metadata.lastSignInTime,
          providers: userCredential.user.providerData.map(provider => provider.providerId),
        });
      }
    } catch (error) {
      setIsSigningIn(false);
      const errObj = error as Record<string, unknown>;
      console.error('[GoogleSignIn] ERROR', {
        message: errObj?.message,
        code: errObj?.code,
        statusCode: errObj?.statusCode,
        nativeErrorCode: errObj?.nativeErrorCode,
        nativeErrorMessage: errObj?.nativeErrorMessage,
        raw: JSON.stringify(error),
      });
      const code =
        typeof error === 'object' && error !== null && 'code' in error
          ? String((error as { code?: string }).code)
          : '';
      if (code === 'SIGN_IN_CANCELLED') throw error;
      throw new Error(getFirebaseErrorMessage(error));
    }
  }, [t]);

  const signInWithApple = useCallback(async () => {
    setIsSigningIn(true);
    try {
      await performAppleSignIn(t);
    } catch (error) {
      setIsSigningIn(false);
      const code =
        typeof error === 'object' && error !== null && 'code' in error
          ? String((error as { code?: string }).code)
          : '';
      if (code === '1001') throw error;
      throw new Error(getFirebaseErrorMessage(error));
    }
  }, [t]);

  const signOut = useCallback(async () => {
    if (__DEV__) console.log('[Tonyte][AuthBoot] manual logout');
    const uid = auth.currentUser?.uid;
    if (uid) await removePushTokenForCurrentDevice(uid).catch(() => undefined);
    await signOutGoogle().catch(() => undefined);
    await firebaseSignOut(auth).catch(() => undefined);
  }, []);

  // Authoritative re-check for the "Check again" button on the Verify Email
  // screen. reload() refreshes the Auth SDK's local user record from the
  // server (picking up emailVerified flips made by clicking the emailed
  // link), and the forced getIdToken(true) refresh is what makes Firestore
  // rules — which read request.auth.token.email_verified off the cached ID
  // token, not the live Auth user — see the new status without waiting for
  // the token's normal ~1h refresh cycle.
  const reloadUser = useCallback(async (): Promise<boolean> => {
    if (!auth.currentUser) return false;
    await reload(auth.currentUser);
    await auth.currentUser.getIdToken(true);
    const verified = auth.currentUser.emailVerified;
    setEmailVerified(verified);
    return verified;
  }, []);

  // Requires an active session — the account stays signed in on the Verify
  // Email screen (unlike the web client, which signs out and asks the user
  // to sign back in), so request.auth is always present here.
  const resendVerificationEmail = useCallback(async (): Promise<void> => {
    try {
      const requestEmailVerificationEmail = httpsCallable(functionsEu, 'requestEmailVerificationEmailEu');
      await requestEmailVerificationEmail();
    } catch (error) {
      throw new Error(getFirebaseErrorMessage(error));
    }
  }, [t]);

  return {
    user,
    loading,
    isSigningIn,
    emailVerified,
    isEmailPasswordAccount: isEmailPasswordAccount(user),
    hasTrustedLinkedProvider: hasTrustedLinkedProvider(user),
    signInWithEmail,
    completePartnerPasswordSetup,
    resetPassword,
    signInWithGoogle,
    signInWithApple,
    signOut,
    reloadUser,
    resendVerificationEmail,
  };
}
